import { createElement, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown, X } from 'lucide-react';
import { format } from 'date-fns';
import { QUESTION_TYPE_ICONS } from '.';
import { isIconValue, iconNameFromValue, getPictureChoiceIcon } from '../../assets/pictureChoiceIcons';
import { sanitizeRichText } from '../../utils/sanitizeHtml';

export function ResponseDetailsSidePanel({
    isOpen,
    onClose,
    response,
    questions,
    currentIndex,
    totalResponses,
    onNext,
    onPrevious,
    endingScreens = []
}) {
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') onClose();
        if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') onPrevious();
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') onNext();
    }, [onClose, onPrevious, onNext]);

    useEffect(() => {
        if (!isOpen) return;
        window.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen || !response) return null;

    const getEndingScreen = () => {
        if (!endingScreens.length) return null;

        if (response.endingScreenId) {
            const savedScreen = endingScreens.find(
                (screen) => screen.id === response.endingScreenId
            );
            if (savedScreen) return savedScreen;
        }

        if (response.score !== undefined && response.score !== null) {
            let matchingScreen = endingScreens.find(
                (screen) =>
                    screen.scoreRange &&
                    response.score >= (screen.scoreRange.min ?? 0) &&
                    response.score <= (screen.scoreRange.max ?? Infinity)
            );
            if (matchingScreen) return matchingScreen;
        }

        let defaultScreen = endingScreens.find(
            (screen) => screen.isDefaultEnding === true
        );

        if (!defaultScreen && endingScreens.length > 0) {
            defaultScreen = endingScreens[0];
        }

        return defaultScreen;
    };

    const userEndingScreen = getEndingScreen();
    const visibleQs = questions.filter(q => q.visible !== false);

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/20 z-40"
                onClick={onClose}
            />

            {/* Side Panel */}
            <div className={`fixed top-0 right-0 h-full w-full sm:w-[540px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-700">Response {currentIndex + 1} of {totalResponses}</span>
                        <div className="text-xs text-gray-500">
                            {response.submittedAt ? format(new Date(response.submittedAt), 'MMM dd, yyyy') : 'No date'}
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={onPrevious}
                            disabled={currentIndex === 0}
                            className={`p-2 bg-white border border-gray-200 rounded-lg shadow-sm transition-all duration-200 ${currentIndex === 0
                                ? 'text-gray-300 cursor-not-allowed opacity-50'
                                : 'text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md'
                            }`}
                        >
                            <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                            onClick={onNext}
                            disabled={currentIndex === totalResponses - 1}
                            className={`p-2 bg-white border border-gray-200 rounded-lg shadow-sm transition-all duration-200 ${currentIndex === totalResponses - 1
                                ? 'text-gray-300 cursor-not-allowed opacity-50'
                                : 'text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md'
                            }`}
                        >
                            <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors ml-1"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="h-full overflow-y-auto pb-20">
                    <div className="p-5">
                        {visibleQs.map((question, idx) => {
                            const iconInfo = QUESTION_TYPE_ICONS[question.type];
                            return (
                                <div key={question.id || idx} className={`py-4 ${idx < visibleQs.length - 1 ? 'border-b border-gray-100' : ''}`}>
                                    <div className="flex items-start gap-3 mb-2.5">
                                        {iconInfo ? (
                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${iconInfo.bgColor}`}>
                                                {createElement(iconInfo.icon, {
                                                    size: 16,
                                                    className: iconInfo.iconColor
                                                })}
                                            </div>
                                        ) : (
                                            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100">
                                                <span className="text-xs text-gray-500">Q</span>
                                            </div>
                                        )}
                                        <span className="text-sm font-medium text-gray-700 leading-relaxed">
                                            {question.text}
                                        </span>
                                    </div>

                                    <div className="ml-10">
                                        <div className="inline-block px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                                            <div className="text-xs text-gray-900 font-medium">
                                                {(() => {
                                                    const answer = response.answers?.[question.id];
                                                    if (answer === undefined || answer === null || answer === '') {
                                                        return <span className="text-gray-400 italic text-xs">No answer provided</span>;
                                                    }
                                                    
                                                    if (question.type === "picture choice" && typeof answer === 'object' && answer.img) {
                                                        const label = answer.label || answer.img;
                                                        // answer.img may be an "icon:Name" token, not a URL.
                                                        const PcIcon = isIconValue(answer.img) ? getPictureChoiceIcon(iconNameFromValue(answer.img)) : null;
                                                        return (
                                                            <div className="flex items-center gap-2">
                                                                {isIconValue(answer.img) ? (
                                                                    <span className="w-6 h-6 flex items-center justify-center rounded border border-gray-200 text-gray-600">
                                                                        {PcIcon ? <PcIcon strokeWidth={1.5} className="w-4 h-4" /> : null}
                                                                    </span>
                                                                ) : (
                                                                    <img
                                                                        src={answer.img}
                                                                        alt={label}
                                                                        className="w-6 h-6 object-cover rounded border border-gray-200"
                                                                    />
                                                                )}
                                                                <span className="text-xs">{label}</span>
                                                            </div>
                                                        );
                                                    }
                                                    
                                                    if (question.type === "star rating") {
                                                        return `${answer}/5`;
                                                    }
                                                    
                                                    if (question.type === "opinion scale") {
                                                        const maxScale = question.options ? Math.max(...question.options.filter(opt => typeof opt === 'number')) : 10;
                                                        return `${answer}/${maxScale}`;
                                                    }

                                                    if (question.type === "nps") {
                                                        const score = parseInt(answer);
                                                        const segment = score <= 6 ? "Detractor" : score <= 8 ? "Passive" : "Promoter";
                                                        const segmentColor = score <= 6 ? "#EF4444" : score <= 8 ? "#F59E0B" : "#22C55E";
                                                        return (
                                                            <span className="flex items-center gap-1.5">
                                                                {answer}/10
                                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ backgroundColor: segmentColor + "20", color: segmentColor }}>
                                                                    {segment}
                                                                </span>
                                                            </span>
                                                        );
                                                    }

                                                    if (question.type === "slider") {
                                                        const unit = question.sliderUnit || "";
                                                        return `${answer}${unit ? ` ${unit}` : ""}`;
                                                    }

                                                    if (question.type === "signature") {
                                                        return answer ? (
                                                            <img src={answer} alt="Signature" className="h-10 border rounded" style={{ maxWidth: "180px" }} />
                                                        ) : "—";
                                                    }

                                                    if (question.type === "matrix" && typeof answer === "object" && answer !== null && !Array.isArray(answer)) {
                                                        return (
                                                            <div className="space-y-0.5 text-sm">
                                                                {Object.entries(answer).map(([row, col]) => (
                                                                    <div key={row}><span className="font-medium">{row}</span>: {col}</div>
                                                                ))}
                                                            </div>
                                                        );
                                                    }

                                                    if ((question.type === "file upload" || question.type === "video audio") && typeof answer === "object" && answer?.url) {
                                                        if (answer.type?.startsWith("video/")) {
                                                            return <video src={answer.url} controls className="max-h-40 rounded" />;
                                                        }
                                                        if (answer.type?.startsWith("audio/")) {
                                                            return <audio src={answer.url} controls className="w-full max-w-xs" />;
                                                        }
                                                        return (
                                                            <a href={answer.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                                                                📎 {answer.name || "File"}
                                                            </a>
                                                        );
                                                    }

                                                    if (question.type === "legal") {
                                                        return answer === true ? "Accepted" : "Not accepted";
                                                    }

                                                    if (question.type === "ranking" && Array.isArray(answer)) {
                                                        return (
                                                            <ol className="list-decimal list-inside text-sm space-y-0.5">
                                                                {answer.map((item, i) => <li key={i}>{item}</li>)}
                                                            </ol>
                                                        );
                                                    }

                                                    if (question.type === "address" && typeof answer === "object" && answer !== null) {
                                                        const parts = [answer.street, answer.city, answer.state, answer.zip, answer.country].filter(Boolean);
                                                        return parts.join(", ") || "—";
                                                    }
                                                    
                                                    if (Array.isArray(answer)) {
                                                        return answer.join(", ");
                                                    }
                                                    
                                                    return String(answer);
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    
                    {userEndingScreen && (
                        <div className="border-t border-gray-100 p-5">
                            <div className="py-4">
                                <div className="flex items-start gap-3 mb-2.5">
                                    <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 shadow-sm">
                                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 leading-relaxed">
                                        Ending Screen
                                    </span>
                                </div>

                                <div className="ml-10">
                                    <div className="inline-block px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm w-full">
                                        <div className="text-xs">
                                            <div className="font-semibold text-gray-900 mb-1">
                                                {userEndingScreen.title}
                                            </div>
                                            {userEndingScreen.description && (
                                                <div
                                                    className="text-gray-600 mb-2 text-xs leading-relaxed"
                                                    dangerouslySetInnerHTML={{
                                                        __html: sanitizeRichText(userEndingScreen.description),
                                                    }}
                                                />
                                            )}
                                            {userEndingScreen.buttonText && (
                                                <div className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                                    {userEndingScreen.buttonText}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className="p-6 border-t border-gray-100 bg-gray-50">
                        <div className="text-sm text-gray-500 mb-2">Response ID</div>
                        <code className="text-sm font-mono text-gray-700 break-all bg-white px-3 py-2 rounded-lg border border-gray-200">{response._id}</code>
                    </div>
                </div>
            </div>
        </>
    );
}
